import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  category: string;
  status: 'considering' | 'planned' | 'in_development' | 'shipped';
  sort_order: number;
  is_visible: boolean;
  icon_name: string | null;
  shipped_at: string | null;
  created_at: string;
  updated_at: string;
}

export type RoadmapStatus = RoadmapItem['status'];

const QUERY_KEY = ['roadmap-items'];

/** Public hook, fetches only visible items */
export function usePublicRoadmap() {
  return useQuery({
    queryKey: [...QUERY_KEY, 'public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .select('*')
        .eq('is_visible', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as RoadmapItem[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Admin hook, fetches all items */
export function useAdminRoadmap() {
  return useQuery({
    queryKey: [...QUERY_KEY, 'admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data as RoadmapItem[];
    },
  });
}

export function useCreateRoadmapItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (item: Omit<RoadmapItem, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .insert(item)
        .select()
        .single();
      if (error) throw error;
      return data as RoadmapItem;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateRoadmapItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<RoadmapItem> & { id: string }) => {
      const { data, error } = await supabase
        .from('roadmap_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as RoadmapItem;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteRoadmapItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('roadmap_items')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
